import { Container, Flex, Text, Heading, Grid, Box } from '@radix-ui/themes';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/fasttrac/contexts/use-tenant-context';
import { useFilteredSkus } from '@trashlab/sitesapp/src/organizations/fasttrac/hooks/use-filtered-skus';
import { useRouter } from '@tanstack/react-router';
import {
  IconRentalCommercial,
  IconRentalResidential,
  IconRentalContractor,
  IconRentalToilet,
} from '@trashlab/sitesapp/src/organizations/fasttrac/template/components/icons/icons';
import { Button } from '@trashlab/sitesapp/src/organizations/fasttrac/template/components/shared/button';
import { useState } from 'react';

export const ServiceOptions = () => {
  const { website, isEditMode } = useTenantContext();
  const router = useRouter();
  const { containerTypeOptions, getTypeDescription, isLoading } =
    useFilteredSkus(website?.organization.id ?? '', '');
  const [hoveredService, setHoveredService] = useState<string | null>(null);

  const getTypeIcon = (typeValue: string) => {
    switch (typeValue) {
      case 'residential':
        return <IconRentalResidential className="w-8 h-8" />;
      case 'commercial':
        return <IconRentalCommercial className="w-8 h-8" />;
      case 'rolloff':
        return <IconRentalContractor className="w-8 h-8" />;
      case 'portableToilet':
        return <IconRentalToilet className="w-8 h-8" />;
      default:
        return <IconRentalResidential className="w-8 h-8" />;
    }
  };

  // Get the variable type for service labels
  const getServiceLabelVariable = (typeValue: string) => {
    switch (typeValue) {
      case 'residential':
        return 'RESIDENTIAL_SERVICE_LABEL';
      case 'commercial':
        return 'COMMERCIAL_SERVICE_LABEL';
      case 'rolloff':
        return 'ROLLOFF_SERVICE_LABEL';
      case 'portableToilet':
        return 'PORTABLE_TOILET_SERVICE_LABEL';
      default:
        return '';
    }
  };

  // Get the variable type for service descriptions
  const getServiceDescriptionVariable = (typeValue: string) => {
    switch (typeValue) {
      case 'residential':
        return 'RESIDENTIAL_SERVICE_DESCRIPTION';
      case 'commercial':
        return 'COMMERCIAL_SERVICE_DESCRIPTION';
      case 'rolloff':
        return 'ROLLOFF_SERVICE_DESCRIPTION';
      case 'portableToilet':
        return 'PORTABLE_TOILET_SERVICE_DESCRIPTION';
      default:
        return '';
    }
  };

  const handleCardClick = async (href: any) => {
    await router.navigate(href);
    await router.invalidate();

    // Wait for the page to load and render, then scroll to dumpster-types section
    setTimeout(() => {
      const dumpsterTypesElement = document.getElementById('dumpster-types');
      if (dumpsterTypesElement) {
        // Calculate offset to account for any fixed headers (adjust as needed)
        const headerOffset = 80;
        const elementPosition =
          dumpsterTypesElement.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 300);
  };

  // Render services in edit mode with descriptions always visible
  const renderEditModeServices = () => {
    return (
      <div className="space-y-2">
        {containerTypeOptions.map((type) => (
          <div key={`service-${type.value}`} className="space-y-2">
            {/* Service Button/Label */}
            <div
              className="p-2 bg-white rounded-md shadow-[0px_7px_26px_rgba(0,0,0,0.08)] border-l-4 border-blue-500"
              data-variable={getServiceLabelVariable(type.value)}
            >
              <div className="flex items-center gap-4">
                {getTypeIcon(type.value)}
                <Text className="text-lg font-semibold leading-none">
                  {type.label}
                </Text>
              </div>
            </div>

            {/* Service Description */}
            <div
              className="p-2 bg-gray-50 rounded-md shadow-[0px_7px_26px_rgba(0,0,0,0.08)] border-l-4 border-gray-400 ml-4"
              data-variable={getServiceDescriptionVariable(type.value)}
            >
              <Text className="text-sm leading-6 text-gray-700">
                {getTypeDescription(type.value)}
              </Text>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (containerTypeOptions.length === 0) {
    return null;
  }

  // Render edit mode if in edit mode
  if (isEditMode) {
    return <Box className="w-full">{renderEditModeServices()}</Box>;
  }

  const totalOptions = containerTypeOptions.length;
  const getFlexBasis = () => {
    switch (totalOptions) {
      case 1:
        return '100%';
      case 2:
        return 'calc(50% - 6px)';
      case 3:
        return 'calc(33.333% - 8px)';
      case 4:
        return 'calc(25% - 9px)';
      default:
        return 'calc(20% - 9.6px)';
    }
  };

  // Normal mode rendering
  return (
    <Box className="w-full relative z-20">
      <Flex wrap="wrap" gap="3" className="w-full relative">
        {containerTypeOptions.map((type) => (
          <Box
            key={type.value}
            className="relative flex-1"
            style={{
              flexBasis: getFlexBasis(),
              zIndex: hoveredService === type.value ? 50 : 1,
            }}
            onMouseEnter={() => setHoveredService(type.value)}
            onMouseLeave={() => setHoveredService(null)}
          >
            <Button
              leftIcon={getTypeIcon(type.value)}
              className="w-full justify-start px-4 py-2"
              title={`View ${type.label} rental options`}
              aria-label={`Browse our ${
                type.label
              } rental services - ${getTypeDescription(type.value)}`}
              onClick={() => handleCardClick(type.href)}
              data-variable={getServiceLabelVariable(type.value)}
            >
              <Text className="font-semibold text-left flex-1">
                {type.label}
              </Text>
            </Button>

            {hoveredService === type.value && (
              <Box
                className="absolute top-full left-0 right-0 mt-1 p-2 bg-white border border-gray-200 rounded-md shadow-lg hidden sm:block"
                style={{
                  minWidth: '200px',
                  zIndex: 9999,
                  position: 'absolute',
                }}
                data-variable={getServiceDescriptionVariable(type.value)}
              >
                <Text size="2" className="text-gray-600 text-center">
                  {getTypeDescription(type.value)}
                </Text>
              </Box>
            )}
          </Box>
        ))}
      </Flex>

      <Box className="sm:hidden mt-4">
        {containerTypeOptions.map((type) => (
          <Box key={`mobile-${type.value}`} className="mb-2">
            <Text size="2" className="text-gray-600">
              <Text
                weight="medium"
                data-variable={getServiceLabelVariable(type.value)}
              >
                {type.label}:
              </Text>{' '}
              <span data-variable={getServiceDescriptionVariable(type.value)}>
                {getTypeDescription(type.value)}
              </span>
            </Text>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ServiceOptions;
